import { MobileSortBottomSheet } from "../../ClientPage/Common/controls/MobileSortBottomSheet";
import { ExportCSV } from "../../Table";
import { useMobileAllColumnsCsv } from "../hooks/useMobileAllColumnsCsv";
import { usePerformanceReportTranslations } from "../hooks/usePerformanceReportTranslations";
import { usePerformanceReportAnalyticsContext } from "../providers/PerformanceReportAnalyticsProvider";
import { usePerformanceReportContext } from "../providers/PerformanceReportProvider";

/**
 * Mobile "Metrics: {key} / Export csv" row (AC-5). This sheet doubles as the
 * row list's single visible-metric picker (`MobileRowList` reads the same
 * `sorting` state) rather than there being a separate "Configure columns"
 * sheet — the updated AC-5 (edited 2026-07 after the design-vs-AC conflict was
 * ruled on) confirms one merged sheet. Sort options cover every metric (radio
 * list + High/Low order), with the percentage metrics grouped under a
 * "Conversion rate" section carrying a description line — matching the
 * canvas's `m-sortgroup`. The trigger reads "Metrics:" and the sheet header
 * "View metrics", both taken from the literal canvas JSX (`m-metric-trig__lab`
 * / `mobile.viewMetrics`) — an earlier version of this component/its memory
 * note claimed "Sort by" matched the canvas, which was incorrect.
 */
export const MobileSortExportRow = () => {
  const translations = usePerformanceReportTranslations();
  const analytics = usePerformanceReportAnalyticsContext();
  const {
    sorting,
    setSorting,
    rows,
    csvFileName,
    filterFns,
    exportCsv,
    isExporting,
    canExport,
  } = usePerformanceReportContext();
  const { columns, dataFormatter } = useMobileAllColumnsCsv();

  const sortOptions = translations.metricColumns.map((c) => ({
    id: c.key,
    label: c.label,
    ...(c.type === "pct"
      ? { section: translations.conversionRate, description: c.menuLabel }
      : {}),
  }));
  const current = sorting[0];
  const selectedSortBy = current?.id ?? "clicks";
  const selectedSortOrder = current?.desc === false ? "asc" : "desc";

  return (
    <div className="flex items-center justify-between gap-4">
      <MobileSortBottomSheet
        sortOptions={sortOptions}
        selectedSortBy={selectedSortBy}
        selectedSortOrder={selectedSortOrder}
        onApply={(sortBy, sortOrder) =>
          setSorting([{ id: sortBy, desc: sortOrder === "desc" }])
        }
        title={translations.mobileMetricsTriggerLabel}
        sheetTitle={translations.mobileViewMetricsTitle}
        isDraggable
      />
      <ExportCSV
        buttonLabel={translations.exportButton}
        data={rows}
        columns={columns}
        dataFormatter={dataFormatter}
        sorting={sorting}
        fileName={csvFileName}
        filterFns={filterFns}
        onExport={
          canExport
            ? () => {
                analytics?.trackExportCsvEvent?.();
                exportCsv({ allColumns: true });
              }
            : undefined
        }
        isExporting={isExporting}
        showIcon
        size="sm"
      />
    </div>
  );
};
